import { useCallback } from 'react';
import { Snapshot, useRecoilValue, useSetRecoilState } from 'recoil';
import { Image3D } from '~/domain/patient/image3d';
import { useStoreResolvedEntity } from '~/hooks/domain/useStoreResolvedEntity';
import useSelectedPatient, {
    readSelectedPatient,
} from '~/hooks/patient/useSelectedPatient';
import {
    primaryImageIdState,
    secondaryImageIdState,
} from '~/state/atoms/image/imageAtoms';

/**
 * Resolves the selected primary {@link ImageId} to the live
 * {@link Image3D} in the patient aggregate. Returns `undefined` when
 * nothing is selected, no patient is loaded, or the id no longer resolves
 * (the image was deleted while selected — holding an id rather than the
 * entity is what makes that a clean `undefined` instead of a dangling
 * reference to an object no longer in the aggregate).
 *
 * The image is the rendering substrate, so most consumers legitimately
 * need the entity (grid geometry, pixel data, slice lookups) rather than
 * a projection. Never mutate it: `setExported` and removal go through
 * `ImageService`. Window/level is the documented exception — it is
 * transient display state owned by the engine.
 */
export function useSelectedPrimaryImage(): Image3D | undefined {
    const patient = useSelectedPatient();
    const imageId = useRecoilValue(primaryImageIdState);
    // Re-resolve on every changeset (so an in-place removal is observed
    // immediately) but re-render only when the resolved image reference
    // actually changes — an unrelated changeset leaves it untouched. See
    // useStoreResolvedEntity.
    return useStoreResolvedEntity(() =>
        patient && imageId
            ? patient.images.find((image) => image.id === imageId)
            : undefined
    );
}

/**
 * The selected primary image as `snapshot` holds it, resolved in the
 * selected patient ({@link readSelectedPatient}). Undefined when no patient
 * or no primary image is selected, or the id no longer resolves. For a
 * callback that must act on the selection of the moment it runs.
 */
export function readSelectedPrimaryImage(
    snapshot: Snapshot
): Image3D | undefined {
    const imageId = snapshot.getLoadable(primaryImageIdState).getValue();
    return imageId === null
        ? undefined
        : readSelectedPatient(snapshot)?.images.find(
              (image) => image.id === imageId
          );
}

/** Secondary (fusion) counterpart to {@link useSelectedPrimaryImage}. */
export function useSelectedSecondaryImage(): Image3D | undefined {
    const patient = useSelectedPatient();
    const imageId = useRecoilValue(secondaryImageIdState);
    return useStoreResolvedEntity(() =>
        patient && imageId
            ? patient.images.find((image) => image.id === imageId)
            : undefined
    );
}

/**
 * Setter for the primary image. Accepts the entity (or `undefined` to
 * clear) and stores only its id, so call sites that used to write the
 * entity into Recoil need no restructuring.
 */
export function useSetPrimaryImage(): (image: Image3D | undefined) => void {
    const setImageId = useSetRecoilState(primaryImageIdState);
    return useCallback(
        (image: Image3D | undefined) => {
            setImageId(image?.id ?? null);
        },
        [setImageId]
    );
}

/** Setter counterpart to {@link useSelectedSecondaryImage}. */
export function useSetSecondaryImage(): (image: Image3D | undefined) => void {
    const setImageId = useSetRecoilState(secondaryImageIdState);
    return useCallback(
        (image: Image3D | undefined) => {
            setImageId(image?.id ?? null);
        },
        [setImageId]
    );
}
